import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Package, FileEdit, MessagesSquare, Inbox, Settings, LogOut, ExternalLink, Menu, X, Loader2, Users, Bell, BellOff, Volume2, VolumeX, Mail, Send, MessageSquareText, Clapperboard, Star, Share2, Facebook, CalendarClock, Images, FileText, Clock, UserCheck, Megaphone, Languages, Smartphone, Link2 } from "lucide-react";
import VideosTab from "./VideosTab";
import SocialTab from "./SocialTab";
import FbTab from "./fb/FbTab";
import LinksTab from "./LinksTab";
import ReviewsTab from "./ReviewsTab";
import { Switch } from "../../components/ui/switch";
import { playChime, unlockAudio, showNotification, notifySupported, getPref, setPref } from "../../lib/alerts";
import AgentsTab from "./AgentsTab";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useSite } from "../../context/SiteContext";
import ProductsTab from "./ProductsTab";
import ContentTab from "./ContentTab";
import ChatsTab from "./ChatsTab";
import { PreviewBanner } from "./PreviewBanner";
import { PhoneAppCard } from "./PhoneAppCard";
import { ContactClicksCard } from "./ContactClicksCard";
import { ChatFeaturesCard } from "./ChatFeaturesCard";
import { setupAdminApp, isStandalone } from "../../lib/push";
import { Dialog, DialogContent, DialogTitle } from "../../components/ui/dialog";
import QuotesTab from "./QuotesTab";
import { MarkPostedDialog } from "./MarkPostedDialog";

const TABS = [
  ["overview", "Overview", LayoutDashboard, false],
  ["products", "Products", Package, true],
  ["content", "Website Content", FileEdit, true],
  ["videos", "Watch & Learn", Clapperboard, true],
  ["social", "Social Media", Share2, true],
  ["fb_schedule", "Daily Schedule", CalendarClock, true],
  ["fb_pages", "FB Pages", Facebook, true],
  ["fb_pictures", "Picture Posts", Images, true],
  ["fb_templates", "FB Templates", FileText, true],
  ["fb_tasks", "My Schedule", Clock, true],
  ["fb_followups", "Follow-Ups", UserCheck, true],
  ["fb_ads", "FB Ads", Megaphone, true],
  ["links", "My Links", Link2, true],
  ["chats", "Live Chat", MessagesSquare, false],
  ["quotes", "Quote Requests", Inbox, false],
  ["reviews", "Reviews", Star, false],
  ["agents", "Team / Agents", Users, true],
  ["settings", "Settings", Settings, false],
];

const Overview = ({ stats, setTab, owner }) => {
  const cards = [
    owner ? ["Products listed", stats.products, "products"] : ["Unread messages", stats.unread_messages, "chats"],
    ["Unread chats", stats.unread_chats, "chats"],
    ["Total chats", stats.chats, "chats"],
    ["New quote requests", stats.new_quotes, "quotes"],
  ];
  return (
    <div>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map(([l, v, t]) => (
          <button key={l} onClick={() => setTab(t)} className="sw-card p-6 text-left" data-testid={`stat-${t}`}>
            <div className="text-[13.5px] text-[#6b7c79]">{l}</div>
            <div className="mt-2 font-jakarta text-[34px] font-extrabold text-[#0f2e2a]">{v ?? "–"}</div>
          </button>
        ))}
      </div>
      <div className="mt-6 rounded-2xl border border-[#e3f0ed] bg-white p-6">
        <h3 className="text-[18px] font-bold text-[#0f2e2a]">Quick actions</h3>
        <div className="mt-4 flex flex-wrap gap-3">
          {owner && <button onClick={() => setTab("products")} className="sw-btn-primary !py-2.5">Add / edit products</button>}
          {owner && <button onClick={() => setTab("content")} className="sw-btn-outline !py-2">Edit website text</button>}
          <button onClick={() => setTab("chats")} className={owner ? "sw-btn-outline !py-2" : "sw-btn-primary !py-2.5"}>Open live chat inbox</button>
          <button onClick={() => setTab("quotes")} className="sw-btn-outline !py-2">View quote requests</button>
        </div>
      </div>
      {owner && <ContactClicksCard />}
    </div>
  );
};

const AlertEmailSettings = () => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testingSms, setTestingSms] = useState(false);
  const testSms = async () => {
    setTestingSms(true);
    try { await api.post("/admin/settings/test-sms"); toast.success(`Test SMS sent to ${st.alert_phone}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to send"); }
    finally { setTestingSms(false); }
  };
  useEffect(() => { api.get("/admin/settings").then((r) => setSt(r.data)); }, []);
  if (!st) return null;
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/admin/settings", st); setSt(data); toast.success("Alert settings saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const test = async () => {
    setTesting(true);
    try { await api.post("/admin/settings/test-email"); toast.success(`Test email sent to ${st.alert_email}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to send"); }
    finally { setTesting(false); }
  };
  return (
    <div className="space-y-4 rounded-2xl border border-[#e3f0ed] bg-white p-6" data-testid="alert-email-settings">
      <div className="flex items-center gap-2"><Mail size={18} className="text-[#0e8c7f]" /><h3 className="text-[18px] font-bold">Email & text alerts</h3></div>
      <p className="text-[14px] text-[#5b6d6a]">Get an email the moment a visitor starts a live chat or submits a quote request.</p>
      <label className="flex items-center gap-3"><Switch checked={st.alerts_enabled} onCheckedChange={(v) => setSt({ ...st, alerts_enabled: v })} data-testid="alerts-enabled-switch" /><span className="text-[14px] font-medium">Send email alerts</span></label>
      <input type="email" className="w-full rounded-xl border border-[#dcebe8] px-4 py-3 text-[15px] outline-none focus:border-[#0e8c7f]" placeholder="alerts@yourbusiness.com" value={st.alert_email} onChange={(e) => setSt({ ...st, alert_email: e.target.value })} data-testid="alert-email-input" />
      <div className="border-t border-[#eef5f3] pt-4">
        <div className="flex items-center gap-2"><Mail size={18} className="text-[#0e8c7f]" /><h3 className="text-[16px] font-bold">Facebook posting emails</h3></div>
        <p className="mt-1 text-[13px] text-[#5b6d6a]">Gets an email at noon (picture) and 7 PM (video) with the file and caption. If this address can't receive mail, the email goes to your alert email above instead.</p>
        <input type="email" className="mt-3 w-full rounded-xl border border-[#dcebe8] px-4 py-3 text-[15px] outline-none focus:border-[#0e8c7f]" placeholder="admin@stripwisepays.com" value={st.post_email || ""} onChange={(e) => setSt({ ...st, post_email: e.target.value })} data-testid="post-email-input" />
      </div>
      <div className="border-t border-[#eef5f3] pt-4">
        <div className="flex items-center gap-2"><MessageSquareText size={18} className="text-[#0e8c7f]" /><h3 className="text-[16px] font-bold">Text (SMS) alerts</h3>
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${st.sms_configured ? "bg-[#dcf7ef] text-[#0b6f65]" : "bg-[#fff3dc] text-[#a0620a]"}`} data-testid="sms-status">{st.sms_configured ? "Connected" : "Not connected"}</span>
        </div>
        {!st.sms_configured && <p className="mt-1 text-[13px] text-[#8a5a06]">SMS sending needs Twilio credentials to be added to the server first.</p>}
        <label className="mt-3 flex items-center gap-3"><Switch checked={!!st.sms_enabled} onCheckedChange={(v) => setSt({ ...st, sms_enabled: v })} data-testid="sms-enabled-switch" /><span className="text-[14px] font-medium">Text me on new chats & quotes</span></label>
        <input type="tel" className="mt-3 w-full rounded-xl border border-[#dcebe8] px-4 py-3 text-[15px] outline-none focus:border-[#0e8c7f]" placeholder="+1 931-412-0007" value={st.alert_phone || ""} onChange={(e) => setSt({ ...st, alert_phone: e.target.value })} data-testid="alert-phone-input" />
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={save} disabled={busy} className="sw-btn-primary !py-2.5 disabled:opacity-60" data-testid="alert-settings-save">{busy && <Loader2 size={16} className="animate-spin" />}Save</button>
        <button onClick={test} disabled={testing} className="sw-btn-outline !py-2 disabled:opacity-60" data-testid="alert-test-btn">{testing ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}Send test email</button>
        <button onClick={testSms} disabled={testingSms || !st.sms_configured} className="sw-btn-outline !py-2 disabled:opacity-50" data-testid="sms-test-btn">{testingSms ? <Loader2 size={16} className="animate-spin" /> : <MessageSquareText size={15} />}Send test SMS</button>
      </div>
    </div>
  );
};

const SettingsTab = ({ owner, alertProps }) => {
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/change-password", f);
      toast.success("Password updated");
      setF({ current_password: "", new_password: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };
  const input = "w-full rounded-xl border border-[#dcebe8] px-4 py-3 text-[15px] outline-none focus:border-[#0e8c7f]";
  const { sound, setSound, notify, enableNotify } = alertProps;
  return (
    <div className="grid max-w-5xl gap-6 lg:grid-cols-2">
    <PhoneAppCard />
    <div className="space-y-4 rounded-2xl border border-[#e3f0ed] bg-white p-6">
      <div className="flex items-center gap-2"><Bell size={18} className="text-[#0e8c7f]" /><h3 className="text-[18px] font-bold">Dashboard alerts (this browser)</h3></div>
      <p className="text-[14px] text-[#5b6d6a]">Keep this dashboard open in a tab to hear a chime and get a desktop notification for new chat messages and quotes.</p>
      <label className="flex items-center gap-3"><Switch checked={sound} onCheckedChange={setSound} data-testid="sound-alert-switch" /><span className="text-[14px] font-medium">Play sound on new messages</span></label>
      <label className="flex items-center gap-3"><Switch checked={notify} onCheckedChange={enableNotify} data-testid="notify-alert-switch" /><span className="text-[14px] font-medium">Show browser notifications</span></label>
      {notifySupported() && Notification.permission === "denied" && <p className="rounded-lg bg-amber-50 px-3 py-2 text-[13px] text-amber-800">Notifications are blocked for this site. Allow them in your browser's site settings.</p>}
      <button type="button" onClick={() => playChime()} className="sw-btn-outline !py-2 text-[14px]"><Volume2 size={15} />Test sound</button>
    </div>
    {owner && <AlertEmailSettings />}
    {owner && <ChatFeaturesCard />}
    <form onSubmit={save} className="space-y-4 rounded-2xl border border-[#e3f0ed] bg-white p-6">
      <h3 className="text-[18px] font-bold">Change password</h3>
      <input type="password" className={input} placeholder="Current password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} data-testid="settings-current-password" />
      <input type="password" className={input} placeholder="New password (min 6)" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} data-testid="settings-new-password" />
      <button disabled={busy} className="sw-btn-primary disabled:opacity-60" data-testid="settings-save">{busy && <Loader2 size={16} className="animate-spin" />}Update password</button>
    </form>
    </div>
  );
};

const AdminDashboard = () => {
  const nav = useNavigate();
  const { refresh, lang, setLang } = useSite();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "overview");
  const [stats, setStats] = useState({});
  const [sound, setSoundState] = useState(() => getPref("sw_alert_sound", true));
  const [notify, setNotifyState] = useState(() => getPref("sw_alert_notify", false) && notifySupported() && Notification.permission === "granted");
  const prev = useRef(null);
  const prefs = useRef({ sound, notify });
  prefs.current = { sound, notify };

  const setSound = (v) => { setPref("sw_alert_sound", v); setSoundState(v); if (v) { unlockAudio(); playChime(); } };
  const enableNotify = async (v) => {
    if (!v) { setPref("sw_alert_notify", false); setNotifyState(false); return; }
    if (!notifySupported()) { toast.error("This browser doesn't support notifications"); return; }
    const perm = Notification.permission === "granted" ? "granted" : await Notification.requestPermission();
    if (perm === "granted") {
      setPref("sw_alert_notify", true); setNotifyState(true);
      showNotification("StripWise alerts enabled", "You'll be notified about new chats and quotes.");
    } else toast.error("Notifications were blocked. Allow them in your browser settings.");
  };

  const [appOpen, setAppOpen] = useState(false);
  const [hideBanner, setHideBanner] = useState(() => localStorage.getItem("sw_hide_install") === "1");
  useEffect(() => { setupAdminApp(); }, []);

  useEffect(() => {
    const unlock = () => unlockAudio();
    window.addEventListener("pointerdown", unlock, { once: true });
    return () => window.removeEventListener("pointerdown", unlock);
  }, []);

  useEffect(() => {
    const back = `/admin?next=${encodeURIComponent(window.location.pathname + window.location.search)}`;
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => { localStorage.removeItem("sw_admin_token"); nav(back); });
  }, [nav]);

  useEffect(() => {
    if (!me) return;
    const load = () => api.get("/admin/stats").then(({ data }) => {
      setStats(data);
      if (navigator.setAppBadge) (data.unread_chats ? navigator.setAppBadge(data.unread_chats) : navigator.clearAppBadge()).catch(() => {});
      const p = prev.current;
      if (p) {
        const newMsg = data.latest_message && data.latest_message.id !== p.msgId && data.unread_messages > 0;
        const newQuote = data.latest_quote && data.latest_quote.created_at !== p.quoteAt;
        if (newMsg || newQuote) {
          if (prefs.current.sound) playChime();
          if (prefs.current.notify) {
            if (newMsg) {
              const m = data.latest_message;
              showNotification("New chat message", m.text || `${m.images?.length || 1} photo(s) received`, () => setTab("chats"));
            }
            if (newQuote) showNotification("New quote request", `From ${data.latest_quote.name}`, () => setTab("quotes"));
          }
          if (newMsg) toast.message("New chat message", { description: data.latest_message.text || "Photo received", action: { label: "Open", onClick: () => setTab("chats") } });
          if (newQuote) toast.message("New quote request", { description: `From ${data.latest_quote.name}` });
        }
      }
      prev.current = { msgId: data.latest_message?.id, quoteAt: data.latest_quote?.created_at };
      const base = "StripWise Admin";
      document.title = data.unread_messages > 0 ? `(${data.unread_messages}) ${base}` : base;
    }).catch(() => {});
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [me]);

  const logout = () => { localStorage.removeItem("sw_admin_token"); nav("/admin"); };
  if (!me) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="animate-spin text-[#0e8c7f]" /></div>;

  const owner = me.role === "admin";
  const tabs = TABS.filter((t) => owner || !t[3]);
  const title = TABS.find((t) => t[0] === tab)?.[1];
  return (
    <>
    <PreviewBanner />
    <div className="min-h-screen bg-[#f4f8f7]" data-testid="admin-dashboard">
      <header className="z-30 border-b md:sticky md:top-0 border-[#e3ecea] bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-[1600px] flex-wrap items-center gap-x-4 gap-y-3 px-3 py-3 md:px-6">
          <img src="/logo.png" alt="StripWise" className="h-6 shrink-0 sm:h-7" />
          <div className="order-2 ml-auto flex items-center gap-1.5 sm:gap-2 xl:order-3 xl:ml-0">
            <button onClick={() => setSound(!sound)} className={`flex h-9 w-9 items-center justify-center rounded-full border transition-colors ${sound ? "border-[#0e8c7f] bg-[#e8f8f4] text-[#0e8c7f]" : "border-[#dcebe8] text-[#8a9a97]"}`} title={sound ? "Sound alerts on" : "Sound alerts off"} aria-label="Toggle sound alerts" data-testid="header-sound-toggle">{sound ? <Volume2 size={16} /> : <VolumeX size={16} />}</button>
            {!isStandalone() && <button onClick={() => setAppOpen(true)} className="flex h-9 items-center gap-1.5 rounded-full bg-[#0e8c7f] px-3 text-[13px] font-semibold text-white transition-colors hover:bg-[#0b6f65]" title="Install the admin as a phone app" data-testid="header-install-app"><Smartphone size={15} /><span>Install</span></button>}
            <button onClick={() => enableNotify(!notify)} className={`flex h-9 w-9 items-center justify-center rounded-full border transition-colors ${notify ? "border-[#0e8c7f] bg-[#e8f8f4] text-[#0e8c7f]" : "border-[#dcebe8] text-[#8a9a97]"}`} title={notify ? "Browser notifications on" : "Enable browser notifications"} aria-label="Toggle notifications" data-testid="header-notify-toggle">{notify ? <Bell size={16} /> : <BellOff size={16} />}</button>
            <div className="flex items-center gap-1 rounded-2xl border border-[#cfe3df] p-1" data-testid="admin-lang-toggle">
              <Languages size={15} className="mx-1 hidden text-[#0e8c7f] sm:block" />
              {["en", "es"].map((l) => (
                <button key={l} onClick={() => setLang(l)} className={`rounded-xl px-2.5 py-1.5 text-[12.5px] font-bold transition-colors ${lang === l ? "bg-[#0e8c7f] text-white" : "text-[#0f2e2a] hover:bg-[#e8f8f4]"}`} data-testid={`admin-lang-${l}`}>{l.toUpperCase()}</button>
              ))}
            </div>
            <Link to="/" target="_blank" className="hidden h-9 items-center px-2 text-[14px] text-[#5b6d6a] hover:text-[#0e8c7f] sm:flex" title="View website" data-testid="admin-view-site"><ExternalLink size={16} /></Link>
            <button onClick={logout} className="px-1 text-[14px] text-[#3d504d] hover:text-[#0e8c7f] sm:px-2" data-testid="admin-logout">Logout</button>
          </div>
          <nav className="order-3 flex w-full flex-wrap justify-center gap-1 xl:order-2 xl:w-auto xl:flex-1" data-testid="admin-nav">
            {tabs.map(([k, l, I]) => (
              <button key={k} onClick={() => setTab(k)} className={`flex items-center gap-2 rounded-full px-3 py-2 text-[13.5px] font-medium transition-colors md:px-4 md:text-[15px] ${tab === k ? "bg-[#0e8c7f] text-white shadow-sm" : "text-[#3d504d] hover:bg-[#e8f8f4] hover:text-[#0b6f65]"}`} data-testid={`admin-tab-${k}`}>
                <I size={17} /> <span>{l}</span>
                {k === "chats" && stats.unread_chats > 0 && <span className="rounded-full bg-[#f59e0b] px-2 text-[11px] font-bold text-white">{stats.unread_chats}</span>}
                {k === "quotes" && stats.new_quotes > 0 && <span className={`rounded-full px-2 text-[11px] font-bold ${tab === k ? "bg-white/25" : "bg-[#e8f1ef]"}`}>{stats.new_quotes}</span>}
              </button>
            ))}
          </nav>
        </div>
      </header>
      {!isStandalone() && !hideBanner && (
        <div className="fixed inset-x-3 bottom-3 z-40 flex items-center gap-3 rounded-2xl bg-[#0f2e2a] p-3 text-white shadow-2xl md:hidden" data-testid="install-banner">
          <img src="/admin-icon-192.png" alt="" className="h-11 w-11 rounded-xl" />
          <div className="min-w-0 flex-1"><div className="text-[14.5px] font-bold">Get the StripWise app</div><div className="text-[12.5px] text-white/75">Alerts for every chat & quote</div></div>
          <button onClick={() => setAppOpen(true)} className="rounded-full bg-[#25D366] px-4 py-2 text-[14px] font-bold text-[#0f2e2a]" data-testid="install-banner-open">Install</button>
          <button onClick={() => { localStorage.setItem("sw_hide_install", "1"); setHideBanner(true); }} className="px-1 text-[20px] leading-none text-white/60" aria-label="Hide" data-testid="install-banner-close">×</button>
        </div>
      )}
      {me && <MarkPostedDialog />}
      <Dialog open={appOpen} onOpenChange={setAppOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto p-0 sm:max-w-lg" data-testid="install-app-dialog">
          <DialogTitle className="sr-only">Install StripWise admin app</DialogTitle>
          <PhoneAppCard />
        </DialogContent>
      </Dialog>
      <div className="mx-auto min-w-0 max-w-[1600px]">
        <div className="flex flex-wrap items-baseline gap-x-3 px-5 pt-6 md:px-8">
          <h1 className="text-[24px] font-bold text-[#0f2e2a] md:text-[30px]" data-testid="admin-page-title">{title}</h1>
          <span className="text-[13px] text-[#6b7c79]">{me.name} · {owner ? "Main admin" : "Agent"} · {me.email}</span>
        </div>
        <main className="p-5 md:p-8">
          {tab === "overview" && <Overview stats={stats} setTab={setTab} owner={owner} />}
          {tab === "products" && owner && <ProductsTab onChange={refresh} />}
          {tab === "content" && owner && <ContentTab onChange={refresh} />}
          {tab === "agents" && owner && <AgentsTab me={me} />}
          {tab === "videos" && owner && <VideosTab />}
          {tab === "social" && owner && <SocialTab />}
          {tab.startsWith("fb_") && owner && <FbTab section={tab.slice(3)} />}
          {tab === "links" && owner && <LinksTab />}
          {tab === "reviews" && <ReviewsTab owner={owner} />}
          {tab === "chats" && <ChatsTab me={me} />}
          {tab === "quotes" && <QuotesTab owner={owner} />}
          {tab === "settings" && <SettingsTab owner={owner} alertProps={{ sound, setSound, notify, enableNotify }} />}
        </main>
      </div>
    </div>
    </>
  );
};

export default AdminDashboard;
